/**
 * Why a file failed, in plain words (R1 of docs/plans/2026-10-19_reader-layout-change.md). The runner's diagnosis of a
 * failed read (import_jobs.diagnosis, migration 0259) names the cell and value the reader raised on, what it expected
 * there, and the file's layout facts; the Files page says them so a plant user sees what changed in the file. The
 * technical part (the raw error, the reader's own line) is for platform admins, under Details.
 */
import type { ImportDiagnosis, ImportDiagnosisOrder } from "@/lib/api/imports";

const ORDER_WORDS: Record<"dmy" | "mdy" | "ymd", string> = {
  dmy: "day first",
  mdy: "month first",
  ymd: "year first",
};

function isPlainOrder(o: ImportDiagnosisOrder | undefined): o is "dmy" | "mdy" | "ymd" {
  return o === "dmy" || o === "mdy" || o === "ymd";
}

function quoted(names: readonly string[]): string {
  const q = names.map((n) => `"${n}"`);
  return q.length <= 1 ? (q[0] ?? "") : `${q.slice(0, -1).join(", ")} and ${q[q.length - 1]}`;
}

/** What a column's written order says, given the order the reader reads it in. */
function orderSentence(column: string, found: ImportDiagnosisOrder, expected: ImportDiagnosisOrder | undefined): string {
  const reads = isPlainOrder(expected) ? ` The reader reads them ${ORDER_WORDS[expected]}.` : "";
  if (found === "ambiguous") {
    return `Column "${column}": its dates could be day first or month first (no day in it is over 12), so the file cannot show which.${reads}`;
  }
  if (found === "conflict") return `Column "${column}" writes its dates in more than one order.${reads}`;
  return `Column "${column}" writes its dates ${ORDER_WORDS[found]}.${reads}`;
}

/** The cell the reader raised on, said as the file shows it. */
function cellSentence(d: ImportDiagnosis): string | null {
  if (!d.value) return null;
  const at = d.column ? `Column "${d.column}"${d.row ? `, data row ${d.row}` : ""}: ` : "";
  if (d.column && isPlainOrder(d.found_order) && isPlainOrder(d.expected_order) && d.found_order !== d.expected_order) {
    return `${at}"${d.value}" is written ${ORDER_WORDS[d.found_order]}; this file's reader reads dates ${ORDER_WORDS[d.expected_order]}.`;
  }
  if (d.column && d.found_order && !isPlainOrder(d.found_order)) {
    return `${at}the reader could not read the date "${d.value}". ${orderSentence(d.column, d.found_order, d.expected_order)}`;
  }
  return at ? `${at}the reader could not read "${d.value}".` : `The reader could not read the value "${d.value}".`;
}

/** Where the update of a failed file's reader stands (R2: the revision it opened or joined, revision_status), in plain
 *  words; "" when it has none. */
export function revisionWords(status: string | undefined): string {
  switch (status) {
    case undefined:
    case "":
      return "";
    case "awaiting_author":
    case "validating":
    case "awaiting_approval":
      return "This file's layout changed, so its reader is being updated. The file imports once a platform admin approves the update.";
    case "done":
      return "Imported by the updated reader.";
    case "failed":
    case "cancelled":
      return "The update of its reader needs a person.";
    default:
      return "The updated reader is importing this file.";
  }
}

/** The plain sentences for everyone, and the technical lines for a platform admin. */
export function diagnosisWords(d: ImportDiagnosis): { plain: string[]; admin: string[] } {
  const plain: string[] = [];
  const cell = cellSentence(d);
  if (cell) plain.push(cell);
  const facts = d.facts ?? {};
  for (const [column, order] of Object.entries(facts.date_orders ?? {})) {
    if (column === d.column) continue; // said with its cell
    if (isPlainOrder(order) && isPlainOrder(d.expected_order) && order === d.expected_order) continue;
    if (!isPlainOrder(d.expected_order) && isPlainOrder(order)) continue; // nothing to compare it with
    plain.push(orderSentence(column, order, d.expected_order));
  }
  const unnamed = facts.unnamed_columns ?? [];
  const atEnd = unnamed.filter((c) => c.at_end);
  if (atEnd.length > 0) {
    const n = atEnd.length;
    const filled = Math.min(...atEnd.map((c) => c.filled_rows));
    const rows = facts.rows ?? 0;
    const who = rows > 0 && filled < rows ? `${filled} of ${rows} rows have` : "Every row has";
    const more = n === 1 ? "one value more" : `${n} values more`;
    const what = n === 1 ? "an unnamed last column" : "unnamed last columns";
    let line = `${who} ${more} than the header names, at the end of the row (${what}).`;
    if (atEnd.some((c) => c.named_empty)) line += ` The header names ${n === 1 ? "it" : "them"} with an empty name.`;
    plain.push(line);
  }
  for (const c of unnamed.filter((u) => !u.at_end)) plain.push(`The header leaves column ${c.position} unnamed.`);
  const missing = facts.missing_columns ?? [];
  if (missing.length > 0) {
    plain.push(
      `This file has no ${missing.length === 1 ? "column" : "columns"} ${quoted(missing)}, which the reader reads.`,
    );
  }
  const added = (facts.added_columns ?? []).filter((c) => !unnamed.some((u) => u.name === c));
  if (added.length > 0) {
    plain.push(`${added.length === 1 ? "A new column" : "New columns"} in this file, not read: ${quoted(added)}.`);
  }
  const admin: string[] = [];
  if (d.error) admin.push(d.error);
  if (d.reader_line?.line) {
    admin.push(`Reader line ${d.reader_line.line}${d.reader_line.code ? `: ${d.reader_line.code}` : ""}`);
  }
  if (d.note) admin.push(d.note);
  return { plain, admin };
}
